/** Производные значения для отрисовки: цвета, подписи, пути SVG. */
import type { Host, TargetStatus } from '@fleet/shared';
import { plural } from '@fleet/shared';
import type { Theme } from './themes';

export { plural };

export const HOSTS: [string, string, string] = ['хост', 'хоста', 'хостов'];
export const SERVERS: [string, string, string] = ['сервер', 'сервера', 'серверов'];
export const ERRORS: [string, string, string] = ['ошибка', 'ошибки', 'ошибок'];
export const ON_HOSTS: [string, string, string] = ['хосте', 'хостах', 'хостах'];
export const SESSIONS: [string, string, string] = ['сессия', 'сессии', 'сессий'];

export function loadColor(cpu: number): string {
  if (cpu >= 90) return 'var(--bad)';
  if (cpu >= 70) return 'var(--warn)';
  if (cpu >= 30) return 'var(--acc)';
  return 'var(--ink4)';
}

export function tempColor(temp: number): string {
  if (temp > 82) return 'var(--bad)';
  if (temp > 70) return 'var(--warn)';
  return 'var(--ink2)';
}

/** Ступень тепловой карты: 0 офлайн, 1 <30, 2 30–69, 3 70–89, 4 90+. */
export function heatIdx(h: Host): 0 | 1 | 2 | 3 | 4 {
  if (h.state === 'offline') return 0;
  if (h.cpu >= 90) return 4;
  if (h.cpu >= 70) return 3;
  if (h.cpu >= 30) return 2;
  return 1;
}

/** Ломаная по значениям: тот же расчёт, что в макете. */
export function svgPath(vals: number[], w: number, h: number, max: number): string {
  const n = vals.length;
  if (n < 2) return '';
  return vals
    .map((v, i) => (i ? 'L' : 'M') + ((i / (n - 1)) * w).toFixed(1) + ' ' + (h - (v / max) * h).toFixed(1))
    .join(' ');
}

export interface HostView {
  host: Host;
  selected: boolean;
  cpuStr: string;
  cpuW: string;
  ramStr: string;
  ramW: string;
  tempStr: string;
  tempColor: string;
  loadColor: string;
  heatBg: string;
  heatFg: string;
  diskStr: string;
  downStr: string;
  stateColor: string;
  cardBd: string;
  boxBg: string;
  boxBd: string;
  tip: string;
}

export function hostView(h: Host, theme: Theme, selected: boolean): HostView {
  const hi = heatIdx(h);
  const off = h.state === 'offline';
  return {
    host: h,
    selected,
    cpuStr: off ? '—' : h.cpu + '%',
    cpuW: h.cpu + '%',
    ramStr: off ? '—' : h.ram + '%',
    ramW: h.ram + '%',
    tempStr: off || h.temp === 0 ? 'нет данных' : h.temp + '°C',
    tempColor: tempColor(h.temp),
    loadColor: loadColor(h.cpu),
    heatBg: theme.heat[hi],
    heatFg: theme.heatInk[hi],
    diskStr: off ? '—' : 100 - h.disk + '%',
    downStr: off ? '—' : h.down + ' Мбит',
    stateColor: off ? 'var(--ink4)' : h.state === 'warn' ? 'var(--warn)' : 'var(--ok)',
    cardBd: selected ? 'var(--acc)' : 'var(--line)',
    boxBg: selected ? 'var(--acc)' : 'transparent',
    boxBd: selected ? 'var(--acc)' : 'var(--line2)',
    tip: `${h.name} · CPU ${h.cpu}% · ${h.temp}°C`
  };
}

const STATUS_LABEL: Record<TargetStatus, string> = {
  pending: 'ожидание',
  running: 'идёт',
  done: 'готово',
  error: 'ошибка'
};

export function statusLabel(s: TargetStatus): string {
  return STATUS_LABEL[s];
}

export function statusColor(s: TargetStatus): string {
  if (s === 'error') return 'var(--bad)';
  if (s === 'done') return 'var(--ok)';
  return 'var(--ink3)';
}

/** Возраст события: «14 мин», «2 ч», «1 д». */
export function ago(iso: string): string {
  if (!iso) return '—';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return 'только что';
  const min = Math.floor(ms / 60_000);
  if (min < 1) return 'только что';
  if (min < 60) return min + ' мин';
  const hours = Math.floor(min / 60);
  if (hours < 24) return hours + ' ч';
  return Math.floor(hours / 24) + ' д';
}

/** Время запуска задачи в человеческом виде: «сегодня 09:12», «23.07 02:00». */
export function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const hhmm = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  if (sameDay) return 'сегодня ' + hhmm;
  const yesterday = new Date(today.getTime() - 86_400_000);
  if (d.toDateString() === yesterday.toDateString()) return 'вчера ' + hhmm;
  return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0') + ' ' + hhmm;
}

/** Ближайший запуск расписания: «сегодня 02:30», «через 2 мин». */
export function nextLabel(iso: string): string {
  const ms = Date.parse(iso) - Date.now();
  if (!Number.isFinite(ms)) return '—';
  if (ms < 0) return 'вот-вот';
  if (ms < 3600_000) return 'через ' + Math.max(1, Math.round(ms / 60_000)) + ' мин';
  return whenLabel(iso);
}

export function pct(part: number, total: number): string {
  if (!total) return '0%';
  return Math.round((part / total) * 100) + '%';
}
